import { useState, useEffect, useCallback } from "react";
import {
    Container,
    Box,
    Typography,
    Stack,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Divider,
    Paper,
    TextField,
    MenuItem,
} from "@mui/material";
import { Icon } from "@iconify/react";
import QRCode from "react-qr-code";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../auth/FirebaseContext";
import { useNavigate } from "react-router-dom";
import {
    getAllByPassengerUID,
    cancelTicketReservationByIds,
    getTicketByTicketIdAPI,
    handleDownloadApi,
    remindLaterReturnTicketAPI,
} from "../api/tickets";
import { getLineStationsByLineId } from "../api/trips";
import OveraDialog from "../components/OveraDialog";
import ExchangeDialog from "../components/ExchangeDialog";
import AppDatePicker from "../components/AppDatePicker";

import { useSnackbar } from "notistack";
import i18n from "../locales/i18n";
import { fmtDate, fmtTime } from "../utils/format";

function timeStrToMinutes(raw) {
    if (raw == null) return null;
    const m = String(raw).match(/(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}


const STATUS_CONFIG = {
    active: { labelKey: "myTickets.active", color: "success", icon: "mdi:check-circle" },
    reservation: { labelKey: "myTickets.reservation", color: "info", icon: "mdi:clock-outline" },
    cancelled: { labelKey: "myTickets.cancelled", color: "warning", icon: "mdi:cancel" },
    refunded: { labelKey: "myTickets.refunded", color: "error", icon: "mdi:cash-refund" },
    expired: { labelKey: "myTickets.expired", color: "default", icon: "mdi:calendar-remove" },
};

function parseTicketDate(ticket) {
    const candidates = [
        ticket.calculatedDepartureTime,
        ticket.journeyDate,
        ticket.date,
    ];

    for (const raw of candidates) {
        if (!raw) continue;
        const d = new Date(raw);
        if (!isNaN(d.getTime()) && d.getFullYear() > 2000) return d;
    }

    const timeOnly = ticket.departureTime || ticket.departureTimeStr;
    const dateOnly = ticket.journeyDate || ticket.date;
    if (timeOnly && dateOnly) {
        const base = dateOnly.split("T")[0];
        const time = typeof timeOnly === "string" && timeOnly.match(/^\d{2}:\d{2}/) ? timeOnly.substring(0, 5) : "00:00";
        const combined = new Date(`${base}T${time}:00`);
        if (!isNaN(combined.getTime())) return combined;
    }

    return null;
}

// Helper — operator polje može biti string (novi payload "SEVERTRANS SOMBOR AD")
// ili objekat (legacy: { operatorName, name }). Vrati labela ili null.
function getOperatorLabel(ticket) {
    if (!ticket) return null;
    if (typeof ticket.operator === "string" && ticket.operator.trim()) return ticket.operator;
    if (ticket.operator && typeof ticket.operator === "object") {
        return ticket.operator.operatorName || ticket.operator.name || null;
    }
    return ticket.operatorName || ticket.carrierName || null;
}

// Helper — stanica ime sa fallback-ima.
function getStationName(ticket, side) {
    if (!ticket) return "";
    const direct = side === "dep" ? ticket.departureStationName : ticket.arrivalStationName;
    if (direct) return direct;
    const obj = side === "dep" ? ticket.departureStation : ticket.arrivalStation;
    if (obj && typeof obj === "object") {
        return obj.stationName || obj.name || "";
    }
    return side === "dep"
        ? (ticket.fromStation || ticket.depStationName || "")
        : (ticket.toStation || ticket.arrStationName || "");
}

// Sedište: numerisan alotman → broj; nenumerisan → "NN" (kao na šalterskoj prodaji).
function seatDisplay(leg) {
    if (!leg) return "";
    if (leg.seatNumbered === false) return "NN";
    return leg.seatReserved || leg.seatNumber || leg.seat || "";
}

// Povratna noga je OVERENA kad ima svoj journeyDate (popunjen pri overi).
function isReturnVerified(leg) {
    return !!leg && (leg.isReturnTicket === true || leg.outboundTicketId != null) && !!(leg.journeyDate || leg.date);
}

/**
 * Rok važenja povratne karte, dana od prodaje. Prikazno pravilo: server overu
 * ne ograničava rokom, pa se ovde odlučuje samo da li neiskorišćena povratna
 * još stoji među predstojećim ili je ispala među obavljena putovanja.
 */
const RETURN_TICKET_VALIDITY_DAYS = 90;

const DAY_MS = 24 * 60 * 60 * 1000;

function liveReturnLeg(ticket) {
    const leg = ticket && ticket.returnTicket;
    if (!leg || leg.refundDt || leg.refunded || leg.isCancelled || leg.cancelled) return null;
    return leg;
}

function hasDepartureClockTime(ticket) {
    if (/\d{2}:\d{2}/.test(String(ticket.calculatedDepartureTime || ""))) return true;
    return /^\d{2}:\d{2}/.test(String(ticket.departureTime || ticket.departureTimeStr || ""));
}

function hasDeparted(ticket, now) {
    const dep = parseTicketDate(ticket);
    if (!dep) return false;
    const ms = dep.getTime();
    return hasDepartureClockTime(ticket) ? ms < now : ms + DAY_MS <= now;
}

function returnValidityExpired(ticket, leg, now) {
    const soldAt = Date.parse(String(ticket.salesDt || leg.salesDt || ""));
    const fallback = parseTicketDate(ticket);
    const from = Number.isFinite(soldAt) ? soldAt : (fallback ? fallback.getTime() : null);
    if (from == null) return false;
    return now - from > RETURN_TICKET_VALIDITY_DAYS * DAY_MS;
}

function getTicketStatus(ticket) {
    if (ticket.refundDt || ticket.refunded) return "refunded";
    if (ticket.isCancelled || ticket.cancelled) return "cancelled";

    const now = Date.now();
    const leg = liveReturnLeg(ticket);
    if (leg) {
        // Karta sa povratnom nogom živi dok se ta noga ne iskoristi. Dok povratak
        // nije overen drži je rok važenja, a kad kupac overi, merodavan je datum
        // povratnog polaska - nikad polazni, koji je tada po pravilu već prošao.
        if (!isReturnVerified(leg)) {
            if (returnValidityExpired(ticket, leg, now)) return "expired";
        } else if (hasDeparted(leg, now)) {
            return "expired";
        }
    } else if (hasDeparted(ticket, now)) {
        return "expired";
    }

    // isReservation is reservationDt from backend - if set, and no salesDt, it's a reservation
    if (ticket.isReservation && !ticket.salesDt) return "reservation";
    return "active";
}

function effectiveTicketDate(ticket) {
    const leg = liveReturnLeg(ticket);
    if (leg && isReturnVerified(leg)) return parseTicketDate(leg) || parseTicketDate(ticket);
    return parseTicketDate(ticket);
}

// Neplaćena rezervacija = sedište zadržano tokom online plaćanja koje kupac nije
// dovršio (pozvao pretragu i zauzeo mesto, ali nije platio karticom). Takva karta
// nije kupčeva — ne prikazuje se u „Moje karte", koje su samo plaćene karte.
// Uslov je namerno na sirovim poljima (ne na getTicketStatus), da uhvati i one
// rezervacije kojima je datum polaska već prošao. Plaćena karta ima salesDt —
// uključujući otvorenu povratnu nogu — pa ostaje.
function isUnpaidReservation(ticket) {
    return !!(ticket && ticket.isReservation && !ticket.salesDt);
}

function TicketDetails({ ticket }) {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [computedArrivalTime, setComputedArrivalTime] = useState(null);
    const [arrivalResolved, setArrivalResolved] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const data = await getTicketByTicketIdAPI(ticket.id);
                setDetails(data?.data || data);
            } catch {
                setDetails(null);
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [ticket.id]);

    // Ako nemamo dolazno vreme, izračunaj ga iz relativnih vremena stanica linije:
    // dolazak = polazni sat + (relArr - relDep), gde su rel* vremena stanica u rasporedu.
    useEffect(() => {
        const merged = { ...ticket, ...(details || {}) };
        setComputedArrivalTime(null);
        setArrivalResolved(false);
        const haveArrival = merged.timeToStation || merged.arrivalTime
            || merged.arrivalTimeStr || merged.calculatedArrivalTime;
        if (haveArrival) { setArrivalResolved(true); return; }

        const depTimeRaw = merged.timeFromStation || merged.calculatedDepartureTime
            || merged.departureTime || merged.departureTimeStr;
        const lineId = merged.lineId || merged.line_id || merged.trip?.line?.id;
        if (!depTimeRaw || !lineId) { setArrivalResolved(true); return; }

        const depId = merged.departureStation?.id || merged.departureStationId || merged.depStationId;
        const arrId = merged.arrivalStation?.id || merged.arrivalStationId || merged.arrStationId;

        let cancelled = false;
        (async () => {
            try {
                const data = await getLineStationsByLineId(lineId);
                if (cancelled) return;
                const stations = data?.content || data || [];
                if (!stations.length) return;
                let depIdx = stations.findIndex((s) => s.stationId === depId || s.station?.id === depId);
                let arrIdx = stations.findIndex((s) => s.stationId === arrId || s.station?.id === arrId);
                if (depIdx === -1) depIdx = 0;
                if (arrIdx === -1) arrIdx = stations.length - 1;
                const relDep = timeStrToMinutes(stations[depIdx]?.departureTime);
                const relArr = timeStrToMinutes(stations[arrIdx]?.arrivalTime);
                const depClock = timeStrToMinutes(fmtTime(depTimeRaw));
                if (relDep == null || relArr == null || depClock == null) return;
                const arrMin = (((depClock + (relArr - relDep)) % 1440) + 1440) % 1440;
                const hh = String(Math.floor(arrMin / 60)).padStart(2, "0");
                const mm = String(arrMin % 60).padStart(2, "0");
                setComputedArrivalTime(`${hh}:${mm}`);
            } catch {
                // tiho — dolazno vreme ostaje prazno
            } finally {
                if (!cancelled) setArrivalResolved(true);
            }
        })();
        return () => { cancelled = true; };
    }, [details, ticket]);

    const handleDownload = async () => {
        if (downloading) return; // spreči višestruke klikove dok PDF nije gotov
        setDownloading(true);
        try {
            const response = await handleDownloadApi(ticket.id);
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `karta-${ticket.ticketNumber || ticket.id}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Greška pri preuzimanju:", err);
            enqueueSnackbar(i18n.t("common.errorDownload"), { variant: "error" });
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    const td = { ...ticket, ...(details || {}) };
    const operatorLabel = getOperatorLabel(td) || td.lineName;
    const ticketTypeLabel = td.ticketTypeName
        || (typeof td.ticketType === "object" ? td.ticketType?.name : td.ticketType);
    // Samo ljudski čitljiv verifikacioni broj — NE forQr (to je dugačak enkriptovan blob koji nosi QR).
    const verificationLabel = td.validationCode || td.verificationNumber;
    const passengerEmail = td.passenger?.passengerEmail || td.passengerEmail || td.email;
    const passengerPhone = td.passenger?.passengerTelephone || td.passengerTelephone || td.telephone || td.phone;

    // Dolazno vreme: gotova vrednost (backend ili izračunato) ili spinner dok se računa.
    const arrivalValue = fmtTime(td.timeToStation || td.arrivalTime || td.arrivalTimeStr) || computedArrivalTime;
    const arrivalPending = !arrivalValue && !arrivalResolved;

    return (
        <Box sx={{ px: 3, py: 2.5, bgcolor: "white" }}>
            {/* Itinerer */}
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "primary.main" }} />
                    <Stack>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {fmtTime(td.timeFromStation || td.calculatedDepartureTime || td.departureTime || td.departureTimeStr) || "--:--"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {getStationName(td, "dep")}
                        </Typography>
                    </Stack>
                </Stack>
                <Box sx={{ flex: 1, height: 2, bgcolor: "primary.main", mx: 2, minWidth: 30 }} />
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "primary.main" }} />
                    <Stack>
                        {arrivalValue ? (
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                {arrivalValue}
                            </Typography>
                        ) : arrivalPending ? (
                            <CircularProgress size={14} />
                        ) : (
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>--:--</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            {getStationName(td, "arr")}
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Detalji u gridu */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr", md: "1fr 1fr 1fr 1fr" },
                    gap: 2,
                    mb: 2,
                }}
            >
                <DetailRow icon="mdi:calendar" label={t("myTickets.details.date")} value={fmtDate(td.journeyDate || td.date)} />
                <DetailRow icon="mdi:bus" label={t("myTickets.details.operator")} value={operatorLabel} />
                <DetailRow icon="mdi:routes" label={t("myTickets.details.line")} value={td.lineName} />
                <DetailRow icon="mdi:tag" label={t("myTickets.details.ticketType")} value={ticketTypeLabel} />
                <DetailRow icon="mdi:seat" label={t("myTickets.details.seat")} value={seatDisplay({ ...td, seatNumbered: td.seatNumbered ?? ticket.seatNumbered })} />
                <DetailRow icon="mdi:ticket-confirmation" label={t("myTickets.details.ticketNumber")} value={td.id || td.ticketNumber} />
                <DetailRow icon="mdi:cash" label={t("myTickets.details.price")} value={
                    td.price ? `${Number(td.price).toLocaleString("sr-Latn", { minimumFractionDigits: 2 })} RSD` : null
                } />
                <DetailRow icon="mdi:phone" label={t("myTickets.details.phone")} value={passengerPhone} />
                <DetailRow icon="mdi:email" label={t("myTickets.details.email")} value={passengerEmail} />
                <DetailRow icon="mdi:clock-check" label={t("myTickets.details.purchasedAt")} value={
                    (td.salesDt || td.salesTime || td.createdAt) ? `${fmtDate(td.salesDt || td.salesTime || td.createdAt)} ${fmtTime(td.salesDt || td.salesTime || td.createdAt)}` : null
                } />
                <DetailRow icon="mdi:bus-clock" label={t("myTickets.details.platform")} value={td.platformNumber} />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* QR za skeniranje na ulazu + istaknut verifikacioni broj (bez potrebe za PDF) */}
            <Stack alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                {(td.forQr || td.forQR) && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Box sx={{ p: 1.5, bgcolor: "#fff", borderRadius: 2, border: "1px solid", borderColor: "grey.300" }}>
                            <QRCode value={String(td.forQr || td.forQR)} size={156} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            {t("confirmation.scanAtGate")}
                        </Typography>
                    </Box>
                )}
                {verificationLabel && (
                    <Box sx={{ textAlign: "center", py: 1.25, px: 3, borderRadius: 2, bgcolor: "rgba(25,118,210,0.06)", border: "1px dashed", borderColor: "primary.main" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
                            {t("myTickets.details.verificationNumber")}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "primary.main", letterSpacing: 2 }}>
                            {verificationLabel}
                        </Typography>
                    </Box>
                )}
            </Stack>

            <Box sx={{ textAlign: "center" }}>
                <Button
                    variant="contained"
                    onClick={handleDownload}
                    disabled={downloading}
                    startIcon={downloading
                        ? <CircularProgress size={16} color="inherit" />
                        : <Icon icon="mdi:download" />}
                    sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none", px: 4 }}
                >
                    {downloading ? t("confirmation.downloading") : t("confirmation.downloadPdf")}
                </Button>
            </Box>
        </Box>
    );
}

function DetailRow({ icon, label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Stack spacing={0.3}>
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Icon icon={icon} width={14} color="#919EAB" />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                    {label}
                </Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600, pl: 2.5 }}>
                {value}
            </Typography>
        </Stack>
    );
}

const STATUS_COLORS = {
    active: "#1a1a2e",
    reservation: "#0288d1",
    cancelled: "#ed6c02",
    refunded: "#d32f2f",
    expired: "#919EAB",
};



function TicketCard({ ticket, onCancel, onExpand, isExpanded, onOvera, onExchange }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [remindLoading, setRemindLoading] = useState(false);
    const status = getTicketStatus(ticket);
    const config = STATUS_CONFIG[status];
    const statusColor = STATUS_COLORS[status];
    const statusLabel = t(config.labelKey);

    const returnTicket = ticket.returnTicket || null;
    const hasReturn = !!returnTicket;
    // Povratna kartica (grupisana ili samostalna otvorena povratna noga).
    const isReturn = hasReturn || ticket.isReturnTicket === true || ticket.outboundTicketId != null;

    const depStation = getStationName(ticket, "dep");
    const arrStation = getStationName(ticket, "arr");
    const operator = getOperatorLabel(ticket) || ticket.lineName || "";
    const ticketNum = ticket.id || ticket.ticketNumber || "";

    // Meta za jednosmernu (negrupisanu) kartu.
    const journeyDate = fmtDate(ticket.journeyDate || ticket.date);
    const depTime = fmtTime(ticket.calculatedDepartureTime || ticket.departureTime || ticket.departureTimeStr);
    const seat = seatDisplay(ticket);

    // Cena: za grupisanu povratnu = zbir polazne + povratne noge.
    const legPrice = Number(ticket.price || ticket.totalPrice || 0);
    const retPrice = hasReturn ? Number(returnTicket.price || 0) : 0;
    const totalPrice = legPrice + retPrice;

    // Kompaktna meta po nozi (jedan red, da povratna kartica ima istu visinu kao jednosmerna).
    const legMeta = (leg) => {
        const d = fmtDate(leg.journeyDate || leg.date);
        const tm = fmtTime(leg.calculatedDepartureTime || leg.departureTime || leg.departureTimeStr);
        const s = seatDisplay(leg);
        if (!d) return t("myTickets.openReturnDate");
        return [tm ? `${d} · ${tm}` : d, s && `${t("myTickets.details.seat")} ${s}`].filter(Boolean).join(" · ");
    };

    // Zamena polaska: samo jednosmerna, plaćena, predstojeća. Status "active" već
    // isključuje neplaćene rezervacije — isReservation se NE sme proveravati posebno,
    // jer i plaćena online karta zadržava reservationDt iz toka kupovine. Rok (2h),
    // "već zamenjena" i kalem presuđuje server — dijalog prikaže poruku odbijanja.
    const exchangeTarget = !isReturn && status === "active" ? ticket : null;

    // Otvorena (neoverena) povratna karta — meta za dugme "Overa".
    const overaTarget = hasReturn
        ? (!returnTicket.journeyDate ? returnTicket : null)
        : ((ticket.isReturnTicket === true || ticket.outboundTicketId != null) && !ticket.journeyDate ? ticket : null);

    const legChipSx = { height: 18, fontSize: "0.56rem", fontWeight: 700, bgcolor: "grey.100", color: "text.secondary", "& .MuiChip-label": { px: 0.8 } };

    return (
        <Box sx={{ mb: 2 }}>
            <Box
                sx={{
                    borderRadius: isExpanded ? "16px 16px 0 0" : 4,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    border: "1px solid",
                    borderColor: "grey.200",
                    borderLeft: "5px solid",
                    borderLeftColor: statusColor,
                    borderBottom: isExpanded ? "none" : undefined,
                    bgcolor: "white",
                    cursor: "pointer",
                    transition: "box-shadow 0.2s",
                    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.1)" },
                }}
                onClick={onExpand}
            >
                {/* Sadrzaj */}
                <Box sx={{ p: { xs: 1.5, md: 2 }, minWidth: 0 }}>
                    {/* Red 1: Ruta + statusi */}
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        {hasReturn ? (
                            <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
                                <Icon icon="mdi:swap-horizontal-bold" width={19} color="#1a1a2e" style={{ flexShrink: 0 }} />
                                <Typography sx={{ fontWeight: 800, fontSize: "0.98rem", lineHeight: 1.3 }}>
                                    {depStation} ⇄ {arrStation}
                                </Typography>
                            </Stack>
                        ) : (
                            <Typography sx={{ fontWeight: 800, fontSize: "1rem", lineHeight: 1.3 }}>
                                {depStation} → {arrStation}
                            </Typography>
                        )}
                        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ flexShrink: 0, ml: 1.5 }}>
                            {isReturn && !hasReturn && (
                                isReturnVerified(ticket) ? (
                                    <Chip
                                        icon={<Icon icon="mdi:check-decagram" width={13} />}
                                        label={t("myTickets.verified")}
                                        size="small"
                                        sx={{ bgcolor: "success.main", color: "white", fontWeight: 700, fontSize: "0.6rem", height: 20, "& .MuiChip-icon": { color: "white", ml: "5px" } }}
                                    />
                                ) : (
                                    <Chip
                                        icon={<Icon icon="mdi:swap-horizontal" width={13} />}
                                        label={t("myTickets.returnTicket")}
                                        size="small"
                                        sx={{ bgcolor: "secondary.main", color: "white", fontWeight: 700, fontSize: "0.6rem", height: 20, "& .MuiChip-icon": { color: "white", ml: "5px" } }}
                                    />
                                )
                            )}
                            <Chip
                                icon={<Icon icon={config.icon} width={13} />}
                                label={statusLabel}
                                size="small"
                                sx={{ bgcolor: statusColor, color: "white", fontWeight: 700, fontSize: "0.6rem", height: 20, "& .MuiChip-icon": { color: "white", ml: "5px" } }}
                            />
                            <Icon icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} width={20} color="#919EAB" />
                        </Stack>
                    </Stack>

                    {/* Red 2: Kompaktna meta — grupisano (Polazak/Povratak u istom redu) ili jednosmerno */}
                    {hasReturn ? (
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.8, "& > *": { display: "flex", alignItems: "center", gap: 0.5 } }}>
                            <Box>
                                <Chip label={t("myTickets.legOutbound")} size="small" sx={legChipSx} />
                                <Typography variant="caption" color="text.secondary">{legMeta(ticket)}</Typography>
                            </Box>
                            <Box>
                                <Chip label={t("myTickets.legReturn")} size="small" sx={legChipSx} />
                                {isReturnVerified(returnTicket) && (
                                    <Chip
                                        icon={<Icon icon="mdi:check-decagram" width={12} />}
                                        label={t("myTickets.verified")}
                                        size="small"
                                        sx={{ height: 18, fontSize: "0.56rem", fontWeight: 700, bgcolor: "success.main", color: "white", "& .MuiChip-icon": { color: "white", ml: "4px" }, "& .MuiChip-label": { px: 0.6 } }}
                                    />
                                )}
                                <Typography variant="caption" color="text.secondary">{legMeta(returnTicket)}</Typography>
                            </Box>
                            {operator && (
                                <Box>
                                    <Icon icon="mdi:bus-side" width={13} color="#919EAB" />
                                    <Typography variant="caption" color="text.secondary">{operator}</Typography>
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.8, "& > *": { display: "flex", alignItems: "center", gap: 0.4 } }}>
                            {journeyDate ? (
                                <Box>
                                    <Icon icon="mdi:calendar" width={14} color="#919EAB" />
                                    <Typography variant="caption" color="text.secondary">{journeyDate}</Typography>
                                </Box>
                            ) : isReturn ? (
                                <Box>
                                    <Icon icon="mdi:calendar-clock" width={14} color="#919EAB" />
                                    <Typography variant="caption" color="text.secondary">{t("myTickets.openReturnDate")}</Typography>
                                </Box>
                            ) : null}
                            {depTime && (
                                <Box>
                                    <Icon icon="mdi:clock-outline" width={14} color="#919EAB" />
                                    <Typography variant="caption" color="text.secondary">{depTime}</Typography>
                                </Box>
                            )}
                            {operator && (
                                <Box>
                                    <Icon icon="mdi:bus-side" width={14} color="#919EAB" />
                                    <Typography variant="caption" color="text.secondary">{operator}</Typography>
                                </Box>
                            )}
                            {seat && (
                                <Box>
                                    <Icon icon="mdi:seat" width={14} color="#919EAB" />
                                    <Typography variant="caption" color="text.secondary">{t("myTickets.details.seat")} {seat}</Typography>
                                </Box>
                            )}
                        </Stack>
                    )}

                    {/* Red 3: Broj karte + cena + otkazi */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t("myTickets.details.ticketNumber")} #{ticketNum}{hasReturn ? ` + #${returnTicket.id}` : ""}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            {status === "reservation" && (
                                <Button
                                    size="small"
                                    color="error"
                                    variant="text"
                                    onClick={(e) => { e.stopPropagation(); onCancel(ticket.id); }}
                                    startIcon={<Icon icon="mdi:cancel" width={14} />}
                                    sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "none", p: 0 }}
                                >
                                    {t("myTickets.cancel")}
                                </Button>
                            )}
                            {totalPrice > 0 && (
                                <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>
                                    {totalPrice.toLocaleString("sr-Latn", { minimumFractionDigits: 2 })} RSD
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Box>

            {isExpanded && (
                <Box sx={{ border: "1px solid", borderColor: "grey.200", borderTop: "none", borderRadius: "0 0 16px 16px", overflow: "hidden" }}>
                    <TicketDetails ticket={ticket} />
                    {exchangeTarget && (
                        <Box sx={{ px: 3, pb: 2, bgcolor: "white" }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => { e.stopPropagation(); onExchange(exchangeTarget); }}
                                startIcon={<Icon icon="mdi:calendar-edit" width={16} />}
                                sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                            >
                                {t("exchange.button")}
                            </Button>
                        </Box>
                    )}
                    {hasReturn && (
                        <>
                            <Divider sx={{ borderStyle: "dashed" }} />
                            <Box sx={{ px: 3, pt: 2, bgcolor: "white" }}>
                                <Chip
                                    icon={<Icon icon="mdi:swap-horizontal" width={14} />}
                                    label={t("myTickets.legReturn")}
                                    size="small"
                                    sx={{ bgcolor: "secondary.main", color: "white", fontWeight: 700, fontSize: "0.62rem", height: 22, "& .MuiChip-icon": { color: "white" } }}
                                />
                            </Box>
                            <TicketDetails ticket={returnTicket} />
                        </>
                    )}
                </Box>
            )}

            {/* Overa — samo za otvorene povratne (bez izabranog polaska); ispod kartice
                da ne zauzima prostor u opisu karte. */}
            {overaTarget && (
                <>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => onOvera(overaTarget)}
                        startIcon={<Icon icon="mdi:ticket-confirmation-outline" width={18} />}
                        sx={{ mt: 1, borderRadius: 2, fontWeight: 700, textTransform: "none", bgcolor: "secondary.main", "&:hover": { bgcolor: "secondary.dark" } }}
                    >
                        {t("myTickets.verifyReturn")}
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        disabled={remindLoading}
                        onClick={async () => {
                            setRemindLoading(true);
                            try {
                                await remindLaterReturnTicketAPI(overaTarget.id);
                                enqueueSnackbar(t("myTickets.remindLaterDone"), { variant: "success" });
                            } catch (err) {
                                enqueueSnackbar(err?.response?.data?.message || t("common.error"), { variant: "error" });
                            } finally {
                                setRemindLoading(false);
                            }
                        }}
                        startIcon={<Icon icon="mdi:bell-sleep-outline" width={16} />}
                        sx={{ mt: 0.5, borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                    >
                        {t("myTickets.remindLater")}
                    </Button>
                </>
            )}
        </Box>
    );
}

export default function MyTicketsPage() {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [expandedId, setExpandedId] = useState(null);
    const [cancelDialog, setCancelDialog] = useState({ open: false, ticketId: null });
    const [cancelling, setCancelling] = useState(false);
    const [filterTicket, setFilterTicket] = useState("");
    const [filterOperator, setFilterOperator] = useState("");
    const [filterDate, setFilterDate] = useState("");
    const [overaTicket, setOveraTicket] = useState(null);
    const [exchangeTicket, setExchangeTicket] = useState(null);

    const tabFilters = [
        { label: t("myTickets.upcoming"), icon: "mdi:clock-outline", filter: (tk) => { const s = getTicketStatus(tk); return s === "active" || s === "reservation"; } },
        { label: t("myTickets.completed"), icon: "mdi:check-circle-outline", filter: (tk) => getTicketStatus(tk) === "expired" },
        { label: t("myTickets.cancelled"), icon: "mdi:cancel", filter: (tk) => { const s = getTicketStatus(tk); return s === "cancelled" || s === "refunded"; } },
    ];

    const fetchTickets = useCallback(async () => {
        if (!user?.uid) return;
        setLoading(true);
        try {
            const response = await getAllByPassengerUID(user.uid);
            const data = response?.data || response;
            const rawTickets = Array.isArray(data) ? data : data?.content || [];
            // Samo plaćene karte — neplaćene rezervacije (nedovršeno online plaćanje)
            // se ne vode kao kupčeve karte.
            const allTickets = rawTickets.filter((tk) => !isUnpaidReservation(tk));
            setTickets(allTickets);
        } catch (error) {
            console.error("Greška pri učitavanju karata:", error);
            enqueueSnackbar(i18n.t("common.errorLoadingTickets"), { variant: "error" });
        } finally {
            setLoading(false);
        }
    }, [user, enqueueSnackbar]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    const handleCancelReservation = async () => {
        if (!cancelDialog.ticketId) return;
        setCancelling(true);
        try {
            await cancelTicketReservationByIds([cancelDialog.ticketId]);
            setCancelDialog({ open: false, ticketId: null });
            fetchTickets();
        } catch (error) {
            console.error("Greška pri otkazivanju:", error);
            enqueueSnackbar(i18n.t("common.errorCancelling"), { variant: "error" });
        } finally {
            setCancelling(false);
        }
    };

    // Grupiši polazak + povratak u jednu stavku: povratnu (otvorenu) kartu vežemo za
    // njenu polaznu preko outboundTicketId → polazna.id. Otvorene povratne nemaju
    // svoj journeyDate/trip (biraju se pri overi), ali su realne karte koje je kupac platio.
    const returnByOutbound = new Map();
    tickets.forEach((tk) => {
        if (tk && tk.outboundTicketId != null) returnByOutbound.set(tk.outboundTicketId, tk);
    });
    const attachedReturnIds = new Set();
    const groupedTickets = tickets
        .filter((tk) => tk && tk.id && tk.outboundTicketId == null)
        .map((tk) => {
            const ret = returnByOutbound.get(tk.id);
            if (ret) {
                attachedReturnIds.add(ret.id);
                return { ...tk, returnTicket: ret };
            }
            return tk;
        });
    // Povratne bez svoje polazne u listi (siročad) — prikaži ih samostalno.
    const orphanReturns = tickets.filter(
        (tk) => tk && tk.id && tk.outboundTicketId != null && !attachedReturnIds.has(tk.id)
    );
    const visibleTickets = [...groupedTickets, ...orphanReturns];

    // Lista prevoznika za dropdown (iz učitanih karata).
    const operatorOptions = Array.from(
        new Set(visibleTickets.map((tk) => getOperatorLabel(tk) || tk.lineName).filter(Boolean))
    ).sort();
    const anyFilter = !!(filterTicket.trim() || filterOperator || filterDate);

    // Filter: broj karte (polazna ili povratna noga), prevoznik, datum putovanja.
    const matchesFilters = (item) => {
        if (filterTicket.trim()) {
            const n = filterTicket.trim();
            const ids = [String(item.id || ""), item.returnTicket ? String(item.returnTicket.id || "") : ""];
            if (!ids.some((id) => id.includes(n))) return false;
        }
        if (filterOperator) {
            if ((getOperatorLabel(item) || item.lineName || "") !== filterOperator) return false;
        }
        if (filterDate) {
            const d = parseTicketDate(item);
            if (!d) return false;
            const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            if (ymd !== filterDate) return false;
        }
        return true;
    };

    // Sortiranje po datumu putovanja: predstojeća (tab 0) rastuće (najskorije gore),
    // ostali tabovi opadajuće (najnovije gore). Karte bez datuma (otvorene) idu na kraj.
    const sortAsc = tab === 0;
    const filteredTickets = visibleTickets
        .filter(tabFilters[tab].filter)
        .filter(matchesFilters)
        .sort((a, b) => {
            const da = effectiveTicketDate(a)?.getTime();
            const db = effectiveTicketDate(b)?.getTime();
            if (da == null && db == null) return 0;
            if (da == null) return 1;
            if (db == null) return -1;
            return sortAsc ? da - db : db - da;
        });

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: 4 }}>
            <Container maxWidth="lg">
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {t("myTickets.title")}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate("/")}
                        startIcon={<Icon icon="mdi:magnify" />}
                        sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }}
                    >
                        {t("myTickets.buyNew")}
                    </Button>
                </Stack>

                <Paper sx={{ borderRadius: 2, mb: 3, overflow: "hidden" }}>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => { setTab(v); setExpandedId(null); }}
                        variant="fullWidth"
                        sx={{
                            "& .MuiTab-root": { fontWeight: 600, textTransform: "none", py: 1.5 },
                        }}
                    >
                        {tabFilters.map((tf, i) => (
                            <Tab key={i} label={`${tf.label} (${visibleTickets.filter(tf.filter).length})`} icon={
                                <Icon icon={tf.icon} width={20} />
                            } iconPosition="start" />
                        ))}
                    </Tabs>
                </Paper>

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ mb: 3 }}
                >
                    <TextField
                        size="small"
                        label={t("myTickets.filterTicketNumber")}
                        value={filterTicket}
                        onChange={(e) => setFilterTicket(e.target.value.replace(/\D/g, ""))}
                        inputProps={{ inputMode: "numeric" }}
                        sx={{ bgcolor: "white", flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        InputProps={{ startAdornment: <Icon icon="mdi:pound" width={18} color="#919EAB" style={{ marginRight: 6 }} /> }}
                    />
                    <TextField
                        select
                        size="small"
                        label={t("myTickets.filterOperator")}
                        value={filterOperator}
                        onChange={(e) => setFilterOperator(e.target.value)}
                        sx={{ bgcolor: "white", flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    >
                        <MenuItem value="">{t("myTickets.filterAllOperators")}</MenuItem>
                        {operatorOptions.map((op) => (
                            <MenuItem key={op} value={op}>{op}</MenuItem>
                        ))}
                    </TextField>
                    <AppDatePicker
                        label={t("myTickets.filterDate")}
                        value={filterDate}
                        onChange={setFilterDate}
                        clearable
                        sx={{ flex: 1, "& .MuiOutlinedInput-root": { bgcolor: "white", height: 40 } }}
                    />
                    {anyFilter && (
                        <Button
                            onClick={() => { setFilterTicket(""); setFilterOperator(""); setFilterDate(""); }}
                            startIcon={<Icon icon="mdi:filter-remove" width={18} />}
                            sx={{ fontWeight: 600, textTransform: "none", whiteSpace: "nowrap" }}
                        >
                            {t("myTickets.filterReset")}
                        </Button>
                    )}
                </Stack>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredTickets.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        {anyFilter
                            ? t("myTickets.noSearchResults")
                            : tab === 0 ? t("myTickets.noUpcoming")
                            : tab === 1 ? t("myTickets.noCompleted")
                            : t("myTickets.noCancelled")}
                    </Alert>
                ) : (
                    <Stack spacing={0}>
                        {filteredTickets.map((ticket) => (
                            <TicketCard
                                key={ticket.id}
                                ticket={ticket}
                                isExpanded={expandedId === ticket.id}
                                onExpand={() => setExpandedId(expandedId === ticket.id ? null : ticket.id)}
                                onCancel={(id) => setCancelDialog({ open: true, ticketId: id })}
                                onOvera={(target) => setOveraTicket(target)}
                                onExchange={(target) => setExchangeTicket(target)}
                            />
                        ))}
                    </Stack>
                )}

                <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, ticketId: null })}>
                    <DialogTitle sx={{ fontWeight: 700 }}>{t("myTickets.cancelTitle")}</DialogTitle>
                    <DialogContent>
                        <Typography>{t("myTickets.cancelConfirm")}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCancelDialog({ open: false, ticketId: null })}>
                            {t("myTickets.cancelNo")}
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleCancelReservation}
                            disabled={cancelling}
                            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {cancelling ? t("myTickets.cancelling") : t("myTickets.cancelYes")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <OveraDialog
                    open={!!overaTicket}
                    ticket={overaTicket}
                    onClose={() => setOveraTicket(null)}
                    onSuccess={() => { setOveraTicket(null); fetchTickets(); }}
                />

                <ExchangeDialog
                    open={!!exchangeTicket}
                    ticket={exchangeTicket}
                    onClose={() => setExchangeTicket(null)}
                    onSuccess={() => {
                        setExchangeTicket(null);
                        enqueueSnackbar(t("exchange.success"), { variant: "success" });
                        fetchTickets();
                    }}
                />
            </Container>
        </Box>
    );
}
